import { Link, useParams } from "react-router-dom";
import { useEffect } from "react";
import { 
  ArrowRight, 
  CheckCircle2, 
  Phone, 
  MessageCircle,
  MapPin,
  Clock,
  Shield,
  Award,
  ChevronRight,
  Paintbrush,
  Home,
  Droplets,
  Layers,
  Star,
  Quote
} from "lucide-react";
import { Button } from "@/components/ui/button";
import LazyImage from "@/components/LazyImage";
import { useLanguage } from "@/i18n/LanguageContext";

// Service data with SEO-optimized content - 6 services: rénovation intérieure, plafonnage, carrelage, peinture, salle de bain, pose de sol
const SERVICES_DATA = {
  "renovation-interieure-bruxelles": {
    slug: "renovation-interieure-bruxelles",
    icon: Home,
    title: "Rénovation Intérieure à Bruxelles",
    subtitle: "Transformation complète de votre habitat",
    metaTitle: "Rénovation Intérieure Bruxelles | Perfect Team Renov | Devis Gratuit",
    metaDescription: "Entreprise de rénovation intérieure à Bruxelles et alentours. Transformation complète : appartements, maisons, sols, murs. 15 ans d'expérience, travail soigné, chantier propre. Devis gratuit 24h.",
    heroImage: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200",
    introduction: `Vous souhaitez **rénover votre intérieur à Bruxelles** ? Perfect Team Renov est votre partenaire de confiance pour tous vos travaux de rénovation. Avec plus de 15 ans d'expérience dans la région bruxelloise et ses environs – Uccle, Ixelles, Waterloo, Rhode-Saint-Genèse, Sint-Pieters-Leeuw – nous transformons vos espaces de vie avec professionnalisme et savoir-faire.

Notre équipe de professionnels qualifiés prend en charge l'ensemble de votre projet, de la conception à la réalisation finale. Ce qui nous distingue : un **travail soigné**, un **chantier toujours propre** et des **délais strictement respectés**. Que ce soit pour une rénovation partielle ou une transformation complète, vous pouvez compter sur notre engagement qualité.`,
    
    services: [
      "Rénovation complète d'appartements et maisons",
      "Transformation d'espaces de vie",
      "Aménagement de combles et sous-sols",
      "Redistribution des pièces",
      "Isolation thermique et acoustique",
      "Coordination complète des travaux"
    ],
    
    process: [
      { step: "1", title: "Visite gratuite", description: "Nous nous déplaçons pour évaluer votre projet et comprendre vos besoins" },
      { step: "2", title: "Devis détaillé", description: "Vous recevez un devis clair et transparent sous 48h" },
      { step: "3", title: "Planification", description: "Nous établissons un planning précis adapté à vos contraintes" },
      { step: "4", title: "Réalisation", description: "Nos équipes exécutent les travaux avec soin et professionnalisme" },
      { step: "5", title: "Réception", description: "Contrôle qualité et livraison du chantier impeccable" }
    ],
    
    advantages: [
      { icon: Shield, title: "Garantie décennale", description: "Tous nos travaux sont couverts par notre assurance" },
      { icon: Clock, title: "Délais respectés", description: "Nous nous engageons sur des délais précis et les tenons" },
      { icon: Award, title: "Artisans qualifiés", description: "Une équipe expérimentée et formée aux dernières techniques" },
      { icon: CheckCircle2, title: "Chantier propre", description: "Protection de vos biens et nettoyage quotidien" }
    ],
    
    faq: [
      { 
        question: "Combien coûte une rénovation intérieure à Bruxelles ?",
        answer: "Le prix d'une rénovation intérieure varie selon l'ampleur des travaux. En moyenne, comptez entre 400€ et 1200€/m² pour une rénovation complète. Nous établissons toujours un devis gratuit et détaillé avant de commencer."
      },
      {
        question: "Combien de temps durent les travaux de rénovation ?",
        answer: "La durée dépend de l'ampleur du projet. Une rénovation d'appartement de 80m² prend généralement entre 6 et 10 semaines. Nous établissons un planning précis dès le départ et nous nous y tenons."
      },
      {
        question: "Intervenez-vous en dehors de Bruxelles ?",
        answer: "Oui, nous intervenons dans toute la région bruxelloise et ses environs : Waterloo, Rhode-Saint-Genèse, Sint-Pieters-Leeuw, Beersel, Drogenbos et toutes les communes limitrophes."
      }
    ],
    
    zones: ["Bruxelles-Ville", "Uccle", "Ixelles", "Waterloo", "Rhode-Saint-Genèse", "Sint-Pieters-Leeuw", "Woluwe-Saint-Pierre", "Woluwe-Saint-Lambert", "Forest", "Etterbeek", "Auderghem", "Watermael-Boitsfort"],
    
    gallery: [
      { before: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=600", after: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600", title: "Appartement Uccle" },
      { before: "https://images.unsplash.com/photo-1560185007-c5ca9d2c014d?w=600", after: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600", title: "Maison Waterloo" }
    ],
    
    testimonial: {
      name: "Marc",
      location: "Ixelles",
      project: "Rénovation appartement 85m²",
      text: "Transformation complète de notre appartement. L'équipe a tout géré de A à Z, le chantier est resté propre pendant toute la durée des travaux. Résultat impeccable.",
      rating: 5
    }
  },
  
  "plafonnage-bruxelles": {
    slug: "plafonnage-bruxelles",
    icon: Layers,
    title: "Plafonnage à Bruxelles",
    subtitle: "Enduits et finitions murales de qualité",
    metaTitle: "Plafonnage Bruxelles | Plafonneurs Professionnels | Perfect Team Renov",
    metaDescription: "Plafonneurs professionnels à Bruxelles et environs. Enduits traditionnels, crépi, lissage. Murs et plafonds parfaitement lisses. Travail soigné, chantier propre. Devis gratuit.",
    heroImage: "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=1200",
    introduction: `Vous recherchez des **plafonneurs professionnels à Bruxelles** ? Perfect Team Renov réalise tous vos travaux de plafonnage avec un savoir-faire reconnu dans toute la région bruxelloise et ses environs : Uccle, Ixelles, Waterloo, Rhode-Saint-Genèse.

Le plafonnage est un métier qui demande précision et expertise. Nos plafonneurs expérimentés maîtrisent toutes les techniques d'enduits pour des murs et plafonds parfaitement lisses. Ce qui fait notre réputation : un **travail soigné jusque dans les moindres détails**, un **chantier toujours propre** et des **finitions impeccables** qui permettent d'appliquer la peinture dans les meilleures conditions.`,
    
    services: [
      "Plafonnage traditionnel au plâtre",
      "Enduits de lissage haute finition",
      "Crépi intérieur décoratif",
      "Réparation de fissures et défauts",
      "Ragréage de murs anciens",
      "Préparation parfaite avant peinture"
    ],
    
    process: [
      { step: "1", title: "Diagnostic", description: "Évaluation de l'état des supports et des travaux nécessaires" },
      { step: "2", title: "Préparation", description: "Protection soignée du chantier et préparation des surfaces" },
      { step: "3", title: "Application", description: "Pose des enduits selon les règles de l'art" },
      { step: "4", title: "Finition", description: "Ponçage et lissage pour un résultat parfait" },
      { step: "5", title: "Nettoyage", description: "Chantier rendu propre et prêt pour la suite" }
    ],
    
    advantages: [
      { icon: Award, title: "Plafonneurs qualifiés", description: "Artisans formés aux techniques traditionnelles et modernes" },
      { icon: CheckCircle2, title: "Finitions parfaites", description: "Des murs lisses et prêts à peindre" },
      { icon: Clock, title: "Délais respectés", description: "Nous terminons dans les temps annoncés" },
      { icon: Shield, title: "Garantie qualité", description: "Travail garanti sans fissures ni défauts" }
    ],
    
    faq: [
      {
        question: "Quelle est la différence entre plafonnage et gyproc ?",
        answer: "Le plafonnage est un enduit appliqué directement sur les murs (briques, blocs). Le gyproc utilise des plaques de plâtre fixées sur une structure. Le plafonnage offre une meilleure solidité et durabilité dans le temps."
      },
      {
        question: "Combien de temps faut-il attendre avant de peindre ?",
        answer: "Il faut généralement attendre 2 à 4 semaines selon l'épaisseur de l'enduit et les conditions de séchage. Nous vous conseillerons sur le délai optimal pour votre situation."
      },
      {
        question: "Intervenez-vous à Waterloo et Rhode-Saint-Genèse ?",
        answer: "Absolument. Nous intervenons dans toute la région bruxelloise et ses environs : Waterloo, Rhode-Saint-Genèse, Sint-Pieters-Leeuw, Uccle, Ixelles et toutes les communes avoisinantes."
      }
    ],
    
    zones: ["Bruxelles", "Uccle", "Ixelles", "Waterloo", "Rhode-Saint-Genèse", "Sint-Pieters-Leeuw", "Anderlecht", "Forest", "Molenbeek", "Jette", "Schaerbeek", "Etterbeek"],
    
    gallery: [
      { before: "https://images.unsplash.com/photo-1560185127-6a8c0c6c4e0a?w=600", after: "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=600", title: "Salon Uccle" }
    ],
    
    testimonial: {
      name: "Philippe",
      location: "Waterloo",
      project: "Plafonnage maison neuve",
      text: "Murs parfaitement lisses, finitions nickel. L'équipe a protégé tous nos sols et meubles. Aucune retouche nécessaire après la peinture.",
      rating: 5
    }
  },

  "carrelage-bruxelles": {
    slug: "carrelage-bruxelles",
    icon: Layers,
    title: "Carrelage à Bruxelles",
    subtitle: "Pose professionnelle de carrelage",
    metaTitle: "Carrelage Bruxelles | Carreleur Professionnel | Perfect Team Renov",
    metaDescription: "Carreleur professionnel à Bruxelles et environs. Pose de carrelage sol et mur, faïence salle de bain, grands formats. Travail soigné et précis. Devis gratuit.",
    heroImage: "https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=1200",
    introduction: `Vous cherchez un **carreleur professionnel à Bruxelles** ? Perfect Team Renov réalise tous vos travaux de carrelage avec précision et savoir-faire. Sols, murs, salles de bain, cuisines – nous posons tous types de carrelage dans la région bruxelloise et ses environs : Uccle, Ixelles, Waterloo, Rhode-Saint-Genèse.

La pose de carrelage demande une expertise technique et un œil pour le détail. Notre équipe maîtrise toutes les techniques : pose droite, diagonale, décalée, grands formats. Ce qui nous distingue : un **travail d'une précision irréprochable**, des **joints parfaitement alignés** et un **chantier laissé propre** à chaque intervention.`,
    
    services: [
      "Carrelage sol intérieur et extérieur",
      "Faïence murale salle de bain et cuisine",
      "Pose de grands formats (60x60, 80x80, 120x60)",
      "Carrelage imitation parquet",
      "Mosaïque et carreaux de ciment",
      "Rénovation et remplacement de carrelage"
    ],
    
    process: [
      { step: "1", title: "Visite et conseil", description: "Évaluation du support et conseils sur le choix du carrelage" },
      { step: "2", title: "Préparation", description: "Ragréage et mise à niveau du sol pour une pose parfaite" },
      { step: "3", title: "Calepinage", description: "Plan de pose optimisé pour un rendu esthétique" },
      { step: "4", title: "Pose", description: "Installation précise avec contrôle niveau constant" },
      { step: "5", title: "Joints et finitions", description: "Jointoiement soigné et nettoyage complet" }
    ],
    
    advantages: [
      { icon: Award, title: "Carreleurs experts", description: "Plus de 15 ans d'expérience en pose de carrelage" },
      { icon: CheckCircle2, title: "Précision millimétrique", description: "Joints alignés et niveau parfait garanti" },
      { icon: Clock, title: "Délais tenus", description: "Planning respecté, pas de mauvaise surprise" },
      { icon: Shield, title: "Garantie pose", description: "Notre travail est garanti contre tout défaut" }
    ],
    
    faq: [
      {
        question: "Quel carrelage choisir pour une salle de bain ?",
        answer: "Pour une salle de bain, privilégiez un carrelage antidérapant (classement R10 minimum) et résistant à l'humidité. Nous vous conseillons sur les meilleurs choix selon votre budget et vos goûts."
      },
      {
        question: "Peut-on poser du carrelage sur un ancien carrelage ?",
        answer: "Oui, c'est possible si l'ancien carrelage est bien adhérent et stable. Nous appliquons un primaire d'accrochage spécial et utilisons une colle adaptée pour garantir une pose durable."
      },
      {
        question: "Combien coûte la pose de carrelage au m² ?",
        answer: "La pose de carrelage coûte entre 35€ et 60€/m² selon la taille des carreaux et la complexité du chantier. Les grands formats et les motifs complexes demandent plus de travail. Devis gratuit sur demande."
      }
    ],
    
    zones: ["Bruxelles", "Uccle", "Ixelles", "Waterloo", "Rhode-Saint-Genèse", "Sint-Pieters-Leeuw", "Woluwe", "Etterbeek", "Auderghem", "Forest", "Saint-Gilles", "Schaerbeek"],
    
    gallery: [
      { before: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=600", after: "https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=600", title: "Salle de bain Ixelles" }
    ],
    
    testimonial: {
      name: "Sophie",
      location: "Rhode-Saint-Genèse",
      project: "Carrelage salle de bain et cuisine",
      text: "Pose de grands carreaux 80x80 dans toute la maison. Joints impeccables, niveau parfait. Très satisfaite du résultat et de la propreté du chantier.",
      rating: 5
    }
  },
  
  "peinture-interieure-bruxelles": {
    slug: "peinture-interieure-bruxelles",
    icon: Paintbrush,
    title: "Peinture Intérieure à Bruxelles",
    subtitle: "Couleurs et finitions professionnelles",
    metaTitle: "Peinture Intérieure Bruxelles | Peintres Professionnels | Perfect Team Renov",
    metaDescription: "Peintres professionnels à Bruxelles et environs. Peinture murs, plafonds, boiseries. Travail soigné, protection maximale, chantier propre. Devis gratuit 24h.",
    heroImage: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200",
    introduction: `Envie de **rafraîchir votre intérieur avec une nouvelle peinture à Bruxelles** ? Perfect Team Renov met à votre disposition une équipe de peintres professionnels pour tous vos travaux de peinture intérieure. Nous intervenons dans toute la région : Uccle, Ixelles, Waterloo, Rhode-Saint-Genèse, Sint-Pieters-Leeuw.

Ce qui fait notre réputation : un **travail soigné** avec une préparation minutieuse des surfaces, une **protection maximale** de vos meubles et sols, et un **chantier rendu propre** à chaque fin de journée. Nous utilisons des peintures de qualité professionnelle pour un résultat durable et esthétique.`,
    
    services: [
      "Peinture murs et plafonds",
      "Laque sur boiseries et portes",
      "Peinture décorative et effets",
      "Mise en peinture après travaux",
      "Rafraîchissement d'appartements",
      "Peinture pour location et vente"
    ],
    
    process: [
      { step: "1", title: "Conseil couleurs", description: "Aide au choix des teintes selon vos goûts et la luminosité" },
      { step: "2", title: "Préparation", description: "Rebouchage, ponçage, sous-couche pour un support parfait" },
      { step: "3", title: "Protection", description: "Bâchage soigné des sols et meubles" },
      { step: "4", title: "Application", description: "Peinture en 2 couches minimum pour un résultat durable" },
      { step: "5", title: "Finitions", description: "Retouches et nettoyage complet du chantier" }
    ],
    
    advantages: [
      { icon: Paintbrush, title: "Peintres experts", description: "Maîtrise de toutes les techniques de peinture" },
      { icon: CheckCircle2, title: "Peintures premium", description: "Marques professionnelles pour un résultat durable" },
      { icon: Shield, title: "Propreté garantie", description: "Protection maximale de vos biens et nettoyage soigné" },
      { icon: Clock, title: "Délais respectés", description: "Nous terminons toujours dans les temps" }
    ],
    
    faq: [
      {
        question: "Quelle peinture choisir pour un salon ?",
        answer: "Pour un salon, nous recommandons une peinture mate ou velours qui offre un rendu élégant et masque les petites imperfections. Le velours est légèrement lavable, idéal pour les pièces de vie."
      },
      {
        question: "Combien de couches de peinture sont nécessaires ?",
        answer: "Nous appliquons toujours minimum 2 couches de finition, précédées d'une sous-couche si nécessaire. Cela garantit une couleur uniforme et une bonne couvrance, même pour des changements de couleur importants."
      },
      {
        question: "Intervenez-vous à Waterloo et Rhode-Saint-Genèse ?",
        answer: "Oui, nous intervenons dans toute la région bruxelloise et ses environs : Waterloo, Rhode-Saint-Genèse, Sint-Pieters-Leeuw, Uccle, Ixelles et toutes les communes avoisinantes."
      }
    ],
    
    zones: ["Bruxelles", "Uccle", "Ixelles", "Waterloo", "Rhode-Saint-Genèse", "Sint-Pieters-Leeuw", "Forest", "Saint-Gilles", "Etterbeek", "Woluwe", "Schaerbeek", "Auderghem"],
    
    gallery: [
      { before: "https://images.unsplash.com/photo-1560185007-c5ca9d2c014d?w=600", after: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600", title: "Salon Ixelles" }
    ],
    
    testimonial: {
      name: "Catherine",
      location: "Uccle",
      project: "Peinture appartement complet",
      text: "Rafraîchissement de tout l'appartement avant mise en location. Travail rapide et soigné, protection parfaite des sols. Je recommande.",
      rating: 5
    }
  },
  
  "salle-de-bain-bruxelles": {
    slug: "salle-de-bain-bruxelles",
    icon: Droplets,
    title: "Rénovation Salle de Bain à Bruxelles",
    subtitle: "Créez votre espace bien-être",
    metaTitle: "Rénovation Salle de Bain Bruxelles | Perfect Team Renov | Devis Gratuit",
    metaDescription: "Rénovation salle de bain à Bruxelles et environs. Douche italienne, carrelage, sanitaires. Transformation clé en main. Travail soigné, chantier propre. Devis gratuit.",
    heroImage: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1200",
    introduction: `Vous rêvez d'une **nouvelle salle de bain à Bruxelles** ? Perfect Team Renov transforme votre salle de bain en un véritable espace bien-être. Nous intervenons dans toute la région : Uccle, Ixelles, Waterloo, Rhode-Saint-Genèse, Sint-Pieters-Leeuw.

La salle de bain est une pièce essentielle de votre logement. Notre équipe coordonne tous les corps de métier pour une rénovation clé en main sans stress. Notre engagement : un **travail soigné jusque dans les moindres détails**, une **étanchéité parfaite** garantie, et un **chantier toujours propre** malgré les travaux de démolition.`,
    
    services: [
      "Douche à l'italienne sur mesure",
      "Remplacement baignoire par douche",
      "Carrelage murs et sols",
      "Installation sanitaires et robinetterie",
      "Meuble vasque sur mesure",
      "Éclairage et ventilation"
    ],
    
    process: [
      { step: "1", title: "Conception", description: "Étude de votre projet et proposition d'aménagement" },
      { step: "2", title: "Démolition", description: "Dépose soignée de l'ancienne salle de bain" },
      { step: "3", title: "Technique", description: "Plomberie, électricité, étanchéité" },
      { step: "4", title: "Carrelage", description: "Pose du carrelage murs et sols avec précision" },
      { step: "5", title: "Finitions", description: "Installation sanitaires, meubles et accessoires" }
    ],
    
    advantages: [
      { icon: Droplets, title: "Étanchéité garantie", description: "Techniques professionnelles anti-infiltrations" },
      { icon: Award, title: "Coordination totale", description: "Tous les corps de métier gérés par nos soins" },
      { icon: CheckCircle2, title: "Travail soigné", description: "Finitions impeccables et attention aux détails" },
      { icon: Clock, title: "Délai maîtrisé", description: "Rénovation complète en 2 à 3 semaines" }
    ],
    
    faq: [
      {
        question: "Combien coûte une rénovation de salle de bain à Bruxelles ?",
        answer: "Le prix varie selon la taille et les finitions. Comptez entre 5.000€ et 15.000€ pour une salle de bain standard de 5-6m². Nous établissons un devis détaillé gratuit après visite."
      },
      {
        question: "Peut-on transformer une baignoire en douche à l'italienne ?",
        answer: "Oui, c'est l'une de nos spécialités. Nous créons une douche à l'italienne avec receveur extra-plat ou encastré, selon la configuration de votre salle de bain et vos souhaits."
      },
      {
        question: "Combien de temps dure une rénovation de salle de bain ?",
        answer: "Une rénovation complète prend généralement 2 à 3 semaines. Nous respectons scrupuleusement le planning établi pour minimiser la gêne occasionnée."
      }
    ],
    
    zones: ["Bruxelles", "Uccle", "Ixelles", "Waterloo", "Rhode-Saint-Genèse", "Sint-Pieters-Leeuw", "Woluwe-Saint-Pierre", "Watermael-Boitsfort", "Auderghem", "Etterbeek", "Forest", "Saint-Gilles"],
    
    gallery: [
      { before: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=600", after: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=600", title: "Salle de bain Uccle" }
    ],
    
    testimonial: {
      name: "Laurent",
      location: "Woluwe-Saint-Pierre",
      project: "Remplacement baignoire par douche italienne",
      text: "Douche à l'italienne installée en 2 semaines. Étanchéité parfaite, carrelage magnifique. L'équipe a géré tous les corps de métier, un vrai confort.",
      rating: 5
    }
  },
  
  "pose-sol-bruxelles": {
    slug: "pose-sol-bruxelles",
    icon: Layers,
    title: "Pose de Sol à Bruxelles",
    subtitle: "Parquet, stratifié et vinyle",
    metaTitle: "Pose de Sol Bruxelles | Parquet et Revêtements | Perfect Team Renov",
    metaDescription: "Pose de parquet et revêtements de sol à Bruxelles et environs. Parquet massif, stratifié, vinyle LVT. Travail soigné, pose impeccable. Devis gratuit.",
    heroImage: "https://images.unsplash.com/photo-1600607687644-c7171b42498f?w=1200",
    introduction: `Vous souhaitez **poser un nouveau sol à Bruxelles** ? Perfect Team Renov installe tous types de revêtements de sol avec un savoir-faire artisanal. Parquet massif, contrecollé, stratifié ou vinyle LVT – nous intervenons dans toute la région : Uccle, Ixelles, Waterloo, Rhode-Saint-Genèse, Sint-Pieters-Leeuw.

Le sol est un élément majeur de votre décoration intérieure. Notre équipe de poseurs expérimentés réalise des poses impeccables avec une **précision millimétrique**. Notre engagement : un **travail soigné**, des **finitions parfaites** (plinthes, seuils, raccords) et un **chantier laissé propre** après notre passage.`,
    
    services: [
      "Parquet massif (chêne, noyer, frêne)",
      "Parquet contrecollé",
      "Stratifié haute qualité",
      "Vinyle et LVT dernière génération",
      "Ponçage et vitrification",
      "Pose de plinthes et seuils"
    ],
    
    process: [
      { step: "1", title: "Conseil", description: "Choix du revêtement selon usage, esthétique et budget" },
      { step: "2", title: "Préparation", description: "Ragréage et mise à niveau du support" },
      { step: "3", title: "Acclimatation", description: "Le parquet s'adapte à l'humidité de la pièce" },
      { step: "4", title: "Pose", description: "Installation précise selon les règles de l'art" },
      { step: "5", title: "Finition", description: "Plinthes, seuils et nettoyage final soigné" }
    ],
    
    advantages: [
      { icon: Layers, title: "Tous types de sols", description: "Expertise sur tous les revêtements de sol" },
      { icon: Award, title: "Pose impeccable", description: "Techniques de pose durables et esthétiques" },
      { icon: CheckCircle2, title: "Matériaux qualité", description: "Parquets et sols de qualité européenne" },
      { icon: Shield, title: "Garantie pose", description: "Notre travail est garanti 2 ans minimum" }
    ],
    
    faq: [
      {
        question: "Quel parquet choisir pour un appartement ?",
        answer: "Pour un appartement, le parquet contrecollé est idéal : stable, compatible chauffage au sol, et moins épais que le massif. Le chêne reste la valeur sûre en termes d'esthétique et durabilité."
      },
      {
        question: "Peut-on poser du parquet sur un ancien carrelage ?",
        answer: "Oui, avec une sous-couche adaptée qui compense les irrégularités et assure l'isolation phonique. Nous vérifions toujours l'état du support avant de vous conseiller."
      },
      {
        question: "Combien coûte la pose de parquet au m² ?",
        answer: "La pose seule coûte entre 25€ et 45€/m² selon le type de pose (flottante, collée, clouée). Le parquet lui-même varie de 30€ à 150€/m² selon la qualité et l'essence choisie."
      }
    ],
    
    zones: ["Bruxelles", "Uccle", "Ixelles", "Waterloo", "Rhode-Saint-Genèse", "Sint-Pieters-Leeuw", "Woluwe-Saint-Pierre", "Woluwe-Saint-Lambert", "Auderghem", "Watermael-Boitsfort", "Etterbeek", "Forest"],
    
    gallery: [
      { before: "https://images.unsplash.com/photo-1560448075-bb485b1d4e36?w=600", after: "https://images.unsplash.com/photo-1600607687644-c7171b42498f?w=600", title: "Parquet Woluwe" }
    ],
    
    testimonial: {
      name: "Thomas",
      location: "Forest",
      project: "Parquet chêne dans tout l'appartement",
      text: "Pose de parquet contrecollé chêne naturel dans 70m². Résultat magnifique, raccords parfaits entre les pièces. Équipe ponctuelle et efficace.",
      rating: 5
    }
  }
};

const ServiceDetailPage = () => {
  const { serviceSlug } = useParams();
  const { t } = useLanguage();
  
  const service = SERVICES_DATA[serviceSlug];
  
  // Update page title and meta description for SEO
  useEffect(() => {
    if (service) {
      document.title = service.metaTitle;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', service.metaDescription);
      }
    }
  }, [service]);
  
  if (!service) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Service non trouvé</h1>
          <Link to="/services">
            <Button className="bg-[#B87333] hover:bg-[#9A5F2A] text-white">
              Voir tous nos services
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const Icon = service.icon;

  return (
    <div className="pt-24 bg-[#0A0A0A]">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={service.heroImage} 
            alt={service.title}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/90 to-transparent" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            {/* Breadcrumb */}
            <nav className="flex items-center space-x-2 text-sm text-white/50 mb-8">
              <Link to="/" className="hover:text-[#B87333]">Accueil</Link>
              <ChevronRight size={14} />
              <Link to="/services" className="hover:text-[#B87333]">Services</Link>
              <ChevronRight size={14} />
              <span className="text-[#B87333]">{service.title}</span>
            </nav>
            
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-[#B87333] to-[#CD7F32] rounded-2xl flex items-center justify-center">
                <Icon className="text-white" size={32} />
              </div>
              <span className="text-[#B87333] text-sm uppercase tracking-widest font-medium">
                {service.subtitle}
              </span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Cormorant_Garamond'] font-bold text-white mb-6">
              {service.title}
            </h1>
            
            {/* CTAs */}
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/devis">
                <Button className="bg-gradient-to-r from-[#B87333] to-[#CD7F32] hover:from-[#CD8B4A] hover:to-[#D4AF37] text-white px-8 py-6 rounded-full">
                  Devis Gratuit 24h
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <a 
                href={`https://wa.me/32488467522?text=${encodeURIComponent(`Bonjour Perfect Team Renov 👋\n\nJe suis intéressé(e) par vos services de ${service.title.toLowerCase()}.\n\nPouvez-vous me recontacter pour un devis gratuit ?\n\nMerci !`)}`}
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="bg-[#25D366] hover:bg-[#20bd5a] text-white px-6 py-6 rounded-full">
                  <MessageCircle className="mr-2" size={18} />
                  WhatsApp
                </Button>
              </a>
              <a href="tel:+32488467522">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white hover:text-[#0A0A0A] px-6 py-6 rounded-full">
                  <Phone className="mr-2" size={18} />
                  +32 488 46 75 22
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-6 bg-[#1A1A1A] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {service.advantages.map((adv, index) => (
              <div key={index} className="flex flex-col items-center">
                <adv.icon className="text-[#B87333] mb-2" size={24} />
                <span className="text-white font-medium text-sm">{adv.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            {service.introduction.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-[#4A4A4A] leading-relaxed mb-6" 
                 dangerouslySetInnerHTML={{ __html: paragraph.replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#0A0A0A]">$1</strong>') }} 
              />
            ))}
          </div>
          
          {/* Services List */}
          <div className="mt-12 bg-[#FAF9F7] rounded-2xl p-8">
            <h2 className="text-2xl font-['Cormorant_Garamond'] font-bold text-[#0A0A0A] mb-6">
              Nos prestations
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {service.services.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle2 className="text-[#B87333] flex-shrink-0" size={20} />
                  <span className="text-[#4A4A4A]">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-[#B87333]/20 text-[#B87333] font-medium text-sm uppercase tracking-widest px-5 py-2 rounded-full mb-6">
              Notre méthode
            </span>
            <h2 className="text-4xl font-['Cormorant_Garamond'] font-bold text-white">
              Comment nous <span className="text-copper-gradient">travaillons</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-5 gap-6">
            {service.process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 mx-auto bg-gradient-to-br from-[#B87333] to-[#CD7F32] rounded-full flex items-center justify-center text-white font-bold text-xl mb-4">
                  {step.step}
                </div>
                <h3 className="text-white font-semibold mb-2">{step.title}</h3>
                <p className="text-white/60 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-20 bg-gradient-to-b from-[#FAF9F7] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-[#B87333]/10 text-[#B87333] font-medium text-sm uppercase tracking-widest px-5 py-2 rounded-full mb-6">
              Pourquoi nous choisir
            </span>
            <h2 className="text-4xl font-['Cormorant_Garamond'] font-bold text-[#0A0A0A]">
              Nos <span className="text-copper-gradient">garanties</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {service.advantages.map((adv, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 bg-gradient-to-br from-[#B87333] to-[#CD7F32] rounded-xl flex items-center justify-center mb-6">
                  <adv.icon className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-semibold text-[#0A0A0A] mb-3">{adv.title}</h3>
                <p className="text-[#6B6B6B]">{adv.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      {service.gallery && service.gallery.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block bg-[#B87333]/10 text-[#B87333] font-medium text-sm uppercase tracking-widest px-5 py-2 rounded-full mb-6">
                Réalisations
              </span>
              <h2 className="text-4xl font-['Cormorant_Garamond'] font-bold text-[#0A0A0A]">
                Avant / <span className="text-copper-gradient">Après</span>
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {service.gallery.map((item, index) => (
                <div key={index} className="bg-[#FAF9F7] rounded-2xl p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs uppercase tracking-wider text-[#B87333] font-medium">Avant</span>
                      <LazyImage 
                        src={item.before} 
                        alt={`Avant - ${item.title}`} 
                        className="w-full h-48 object-cover rounded-xl mt-2" 
                        wrapperClassName="w-full h-48 rounded-xl mt-2"
                      />
                    </div>
                    <div>
                      <span className="text-xs uppercase tracking-wider text-[#B87333] font-medium">Après</span>
                      <LazyImage 
                        src={item.after} 
                        alt={`Après - ${item.title}`} 
                        className="w-full h-48 object-cover rounded-xl mt-2" 
                        wrapperClassName="w-full h-48 rounded-xl mt-2"
                      />
                    </div>
                  </div>
                  <p className="text-center text-[#0A0A0A] font-medium mt-4">{item.title}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonial */}
      {service.testimonial && (
        <section className="py-20 bg-[#0A0A0A]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-block bg-[#B87333]/20 text-[#B87333] font-medium text-sm uppercase tracking-widest px-5 py-2 rounded-full mb-6">
                Avis client
              </span>
              <h2 className="text-4xl font-['Cormorant_Garamond'] font-bold text-white">
                Ce qu'en disent <span className="text-copper-gradient">nos clients</span>
              </h2>
            </div>
            
            <div className="bg-[#1A1A1A] rounded-3xl p-10 relative">
              <Quote className="absolute top-6 left-6 text-[#B87333]/30" size={48} />
              
              <div className="relative z-10">
                <div className="flex items-center gap-1 mb-6 justify-center">
                  {[...Array(service.testimonial.rating)].map((_, i) => (
                    <Star key={i} className="text-[#E07B39] fill-[#E07B39]" size={20} />
                  ))}
                </div>
                
                <p className="text-xl text-white/90 text-center leading-relaxed mb-8 font-light italic">
                  "{service.testimonial.text}"
                </p>
                
                <div className="text-center">
                  <p className="text-white font-semibold text-lg">{service.testimonial.name}</p>
                  <p className="text-[#B87333]">{service.testimonial.location}</p>
                  <p className="text-white/60 text-sm mt-1">{service.testimonial.project}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-20 bg-[#FAF9F7]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-[#B87333]/10 text-[#B87333] font-medium text-sm uppercase tracking-widest px-5 py-2 rounded-full mb-6">
              FAQ
            </span>
            <h2 className="text-4xl font-['Cormorant_Garamond'] font-bold text-[#0A0A0A]">
              Questions <span className="text-copper-gradient">fréquentes</span>
            </h2>
          </div>
          
          <div className="space-y-6">
            {service.faq.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-md">
                <h3 className="text-lg font-semibold text-[#0A0A0A] mb-4">{item.question}</h3>
                <p className="text-[#6B6B6B] leading-relaxed">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Zones */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-['Cormorant_Garamond'] font-bold text-[#0A0A0A] flex items-center justify-center gap-2">
              <MapPin className="text-[#B87333]" size={24} />
              Zones d'intervention
            </h2>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3">
            {service.zones.map((zone, index) => (
              <span key={index} className="px-4 py-2 bg-[#FAF9F7] border border-[#E8E8E8] rounded-full text-[#4A4A4A] text-sm hover:border-[#B87333] hover:text-[#B87333] transition-colors">
                {zone}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#B87333] to-[#8B5A2B]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-['Cormorant_Garamond'] font-bold text-white mb-6">
            Prêt à démarrer votre projet ?
          </h2>
          <p className="text-xl text-white/90 mb-10">
            Contactez-nous pour un devis gratuit et sans engagement. Réponse sous 24h.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/devis">
              <Button className="bg-white text-[#B87333] hover:bg-white/90 px-10 py-7 text-lg rounded-full shadow-xl">
                Demander un Devis Gratuit
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <a href="tel:+32488467522">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#B87333] px-8 py-7 text-lg rounded-full">
                <Phone className="mr-2" size={20} />
                +32 488 46 75 22
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;

// Export services data for use in other components
export { SERVICES_DATA };
